export type LegacyPublicPath = '/end-user-policy' | '/privacy-policy' | '/terms-of-service' | '/learn-more';

export type LegacyContentBlock =
  | { kind: 'heading' | 'subheading' | 'paragraph'; text: string }
  | { kind: 'unordered-list' | 'ordered-list'; text: string; items: string[] };

export interface LegacyPublicDocument {
  title: string;
  description: string;
  sourceUrl: string;
  capturedAt: '2026-08-29';
  sourceSha256: string;
  effectiveDate?: string;
  lastUpdated?: string;
  blocks: LegacyContentBlock[];
  download?: { href: string; label: string; sha256: string };
}

/** Captured from the already-published enshield.com pages on 2026-08-29. */
export const LEGACY_PUBLIC_DOCUMENTS: Record<LegacyPublicPath, LegacyPublicDocument> = {
  "/end-user-policy": {
    "title": "End User Policy",
    "description": "The published Enshield Risk Solutions Package Protection Policy for protected shipments.",
    "sourceUrl": "https://enshield.com/end-user-policy",
    "capturedAt": "2026-08-29",
    "sourceSha256": "aa94da6c07c42dbd5abb5d23d3ce176cb9ed1bdb0fe6a4c3d1914838a0bf8aaa",
    "blocks": [
      {
        "kind": "paragraph",
        "text": "Package Protection Policy published by Enshield Risk Solutions."
      }
    ],
    "download": {
      "href": "/legal/end-user-policy-2026-08-19.pdf",
      "label": "Download End User Policy PDF",
      "sha256": "aa94da6c07c42dbd5abb5d23d3ce176cb9ed1bdb0fe6a4c3d1914838a0bf8aaa"
    }
  },
  "/privacy-policy": {
    "title": "Enshield Inc. Privacy Policy",
    "description": "The published Enshield Inc. Privacy Policy describing information collection, use, disclosure, retention, security, and privacy rights.",
    "sourceUrl": "https://enshield.com/privacy-policy",
    "capturedAt": "2026-08-29",
    "sourceSha256": "936e4d3061012566e01b5779f314ea038be0356d1d08e2cfe767c2eac2ea8b73",
    "effectiveDate": "April 24, 2026",
    "lastUpdated": "April 24, 2026",
    "blocks": [
      {
        "kind": "heading",
        "text": "1. Introduction"
      },
      {
        "kind": "paragraph",
        "text": "Enshield Inc. (\"Enshield,\" \"we,\" \"our,\" or \"us\") respects the privacy and security of merchant, consumer, claimant, and visitor information. This Privacy Policy explains how we collect, use, disclose, store, process, and protect information obtained through our websites, applications, integrations, APIs, merchant onboarding systems, claims administration systems, ACH billing processes, shipment protection platform, and related services (collectively, the “Services”)."
      },
      {
        "kind": "paragraph",
        "text": "Because Enshield operates in industries involving e-commerce, payment processing, shipment protection administration, ACH transactions, and regulated insurance-related activities, we maintain privacy and information handling practices designed to support compliance with applicable laws, regulations, contractual obligations, industry standards, and network requirements."
      },
      {
        "kind": "paragraph",
        "text": "By accessing or using the Services, users acknowledge and agree to the practices described in this Privacy Policy."
      },
      {
        "kind": "heading",
        "text": "2. Scope"
      },
      {
        "kind": "paragraph",
        "text": "This Privacy Policy applies to information collected, processed, stored, transmitted, or maintained by Enshield in connection with:"
      },
      {
        "kind": "unordered-list",
        "text": "The Enshield website, merchant portal, APIs, applications, integrations, and software platform; Merchant onboarding, underwriting support, account administration, and billing processes; Shipment protection program administration and related customer support activities; Claims intake, investigation, review, reimbursement, and resolution workflows; ACH authorization, billing, payment processing coordination, and transaction administration; Communications between Enshield, merchants, customers, carriers, financial institutions, service providers, and claimants; Fraud prevention, compliance reviews, identity verification, and risk monitoring activities; Marketing, business development, and support interactions; Any other services provided by Enshield in connection with its shipment protection administration and merchant services platform.",
        "items": [
          "The Enshield website, merchant portal, APIs, applications, integrations, and software platform;",
          "Merchant onboarding, underwriting support, account administration, and billing processes;",
          "Shipment protection program administration and related customer support activities;",
          "Claims intake, investigation, review, reimbursement, and resolution workflows;",
          "ACH authorization, billing, payment processing coordination, and transaction administration;",
          "Communications between Enshield, merchants, customers, carriers, financial institutions, service providers, and claimants;",
          "Fraud prevention, compliance reviews, identity verification, and risk monitoring activities;",
          "Marketing, business development, and support interactions;",
          "Any other services provided by Enshield in connection with its shipment protection administration and merchant services platform."
        ]
      },
      {
        "kind": "paragraph",
        "text": "This Privacy Policy applies to:"
      },
      {
        "kind": "unordered-list",
        "text": "Merchants and businesses using the Enshield platform; Customers purchasing shipment or package protection through merchants utilizing Enshield services; Claimants submitting claims or reimbursement requests; Website visitors and platform users; Vendors, partners, and service providers interacting with Enshield; Individuals communicating with Enshield regarding support, claims, billing, compliance, or business inquiries.",
        "items": [
          "Merchants and businesses using the Enshield platform;",
          "Customers purchasing shipment or package protection through merchants utilizing Enshield services;",
          "Claimants submitting claims or reimbursement requests;",
          "Website visitors and platform users;",
          "Vendors, partners, and service providers interacting with Enshield;",
          "Individuals communicating with Enshield regarding support, claims, billing, compliance, or business inquiries."
        ]
      },
      {
        "kind": "paragraph",
        "text": "This Privacy Policy does not apply to:"
      },
      {
        "kind": "unordered-list",
        "text": "Third-party websites or services not controlled by Enshield; Banks, payment processors, or financial institutions operating under their own privacy policies; Insurance carriers or regulated entities maintaining separate legal obligations; E-commerce platforms or merchant storefronts operating independently of Enshield; Shipping carriers or logistics providers subject to separate privacy practices.",
        "items": [
          "Third-party websites or services not controlled by Enshield;",
          "Banks, payment processors, or financial institutions operating under their own privacy policies;",
          "Insurance carriers or regulated entities maintaining separate legal obligations;",
          "E-commerce platforms or merchant storefronts operating independently of Enshield;",
          "Shipping carriers or logistics providers subject to separate privacy practices."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Users should review the privacy policies of applicable third parties separately."
      },
      {
        "kind": "heading",
        "text": "3. Information We Collect"
      },
      {
        "kind": "subheading",
        "text": "A. Information Provided Directly"
      },
      {
        "kind": "paragraph",
        "text": "We may collect information voluntarily provided by merchants, customers, claimants, vendors, or other users, including:"
      },
      {
        "kind": "unordered-list",
        "text": "Name Business name Employer identification number (EIN) Business ownership and beneficial ownership information Email address Phone number Billing address and shipping address Consumer order information provided during checkout with participating merchants Payment and billing information Limited ACH authorization and payment-related information necessary to facilitate billing and payment processing through third-party payment processors Claim-related documentation and communications Government-issued identification where legally required Tax and compliance documentation Account credentials and authentication information Customer support communications Call recordings and communications where permitted by applicable law AI-generated meeting notes, summaries, and meeting transcripts Carrier tracking and shipment information Product, transaction, and order details related to package protection purchases",
        "items": [
          "Name",
          "Business name",
          "Employer identification number (EIN)",
          "Business ownership and beneficial ownership information",
          "Email address",
          "Phone number",
          "Billing address and shipping address",
          "Consumer order information provided during checkout with participating merchants",
          "Payment and billing information",
          "Limited ACH authorization and payment-related information necessary to facilitate billing and payment processing through third-party payment processors",
          "Claim-related documentation and communications",
          "Government-issued identification where legally required",
          "Tax and compliance documentation",
          "Account credentials and authentication information",
          "Customer support communications",
          "Call recordings and communications where permitted by applicable law",
          "AI-generated meeting notes, summaries, and meeting transcripts",
          "Carrier tracking and shipment information",
          "Product, transaction, and order details related to package protection purchases"
        ]
      },
      {
        "kind": "subheading",
        "text": "B. Information Collected Automatically"
      },
      {
        "kind": "paragraph",
        "text": "When users interact with the Services, we may automatically collect:"
      },
      {
        "kind": "unordered-list",
        "text": "IP address Browser type and device identifiers Operating system Access timestamps and activity logs Session analytics and usage metrics Cookies and similar tracking technologies Fraud and risk indicators Geolocation information derived from IP address Transaction metadata Referral URLs and interaction history",
        "items": [
          "IP address",
          "Browser type and device identifiers",
          "Operating system",
          "Access timestamps and activity logs",
          "Session analytics and usage metrics",
          "Cookies and similar tracking technologies",
          "Fraud and risk indicators",
          "Geolocation information derived from IP address",
          "Transaction metadata",
          "Referral URLs and interaction history"
        ]
      },
      {
        "kind": "paragraph",
        "text": "Enshield may also use communication, conferencing, support, or AI-assistance technologies that generate call recordings, voicemail records, automated meeting summaries, transcripts, chat logs, or AI-generated notes in connection with customer support, merchant onboarding, sales discussions, compliance reviews, claims handling, operational meetings, training, quality assurance, and business communications."
      },
      {
        "kind": "paragraph",
        "text": "Where required by applicable law, Enshield will seek appropriate consent or provide notice regarding call recording practices."
      },
      {
        "kind": "subheading",
        "text": "C. Information Received From Third Parties"
      },
      {
        "kind": "paragraph",
        "text": "We may receive information from:"
      },
      {
        "kind": "unordered-list",
        "text": "E-commerce platforms and integrations Shipping carriers and logistics providers Payment processors and ACH processors Financial institutions Fraud prevention and identity verification providers Insurance carriers and regulated program partners Analytics and security providers Merchants and storefront systems Government agencies or regulatory authorities when legally required",
        "items": [
          "E-commerce platforms and integrations",
          "Shipping carriers and logistics providers",
          "Payment processors and ACH processors",
          "Financial institutions",
          "Fraud prevention and identity verification providers",
          "Insurance carriers and regulated program partners",
          "Analytics and security providers",
          "Merchants and storefront systems",
          "Government agencies or regulatory authorities when legally required"
        ]
      },
      {
        "kind": "subheading",
        "text": "D. Sensitive and Regulated Information"
      },
      {
        "kind": "paragraph",
        "text": "Certain information processed in connection with Enshield services may be subject to heightened legal, financial, insurance, or payment network requirements, including:"
      },
      {
        "kind": "unordered-list",
        "text": "Payment transaction records; ACH authorization confirmations; Claim documentation; Insurance or risk administration records; Fraud prevention data; Identity verification records; Transaction and reimbursement histories.",
        "items": [
          "Payment transaction records;",
          "ACH authorization confirmations;",
          "Claim documentation;",
          "Insurance or risk administration records;",
          "Fraud prevention data;",
          "Identity verification records;",
          "Transaction and reimbursement histories."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Enshield is designed to minimize the direct collection and storage of sensitive financial account information. Bank account credentials, full account numbers, and certain ACH authorization data are intended to be collected and processed through secure third-party payment processors and financial service providers, including Stripe and its affiliated services, using their hosted or embedded payment interfaces and security controls."
      },
      {
        "kind": "paragraph",
        "text": "In connection with package protection services, Enshield may collect and store basic consumer transaction and order information provided by consumers at the time of purchase through participating merchant checkout systems. This information may include:"
      },
      {
        "kind": "unordered-list",
        "text": "Consumer name; Shipping and billing address; Email address and contact information; Order number and transaction details; Shipment and carrier tracking information; Product and purchase information associated with the protected order.",
        "items": [
          "Consumer name;",
          "Shipping and billing address;",
          "Email address and contact information;",
          "Order number and transaction details;",
          "Shipment and carrier tracking information;",
          "Product and purchase information associated with the protected order."
        ]
      },
      {
        "kind": "paragraph",
        "text": "This information is used to administer package protection services, verify coverage eligibility, process claims, communicate with consumers and merchants, investigate fraud, and maintain operational records."
      },
      {
        "kind": "paragraph",
        "text": "Enshield does not intentionally store full bank account credentials or sensitive payment authentication information on Enshield-operated systems except as reasonably necessary for transaction verification, reconciliation, compliance, dispute handling, or legal obligations."
      },
      {
        "kind": "paragraph",
        "text": "Enshield limits access to regulated or sensitive information to authorized personnel and service providers with a legitimate business need."
      },
      {
        "kind": "heading",
        "text": "4. How We Use Information"
      },
      {
        "kind": "paragraph",
        "text": "We use collected information for legitimate business, contractual, legal, operational, and regulatory purposes, including:"
      },
      {
        "kind": "unordered-list",
        "text": "Providing and administering the Services; Operating shipment protection and related administrative programs; Processing claims, reimbursements, and customer support requests; Facilitating merchant onboarding, account verification, and risk assessment; Managing ACH billing and payment transactions; Maintaining compliance with NACHA Operating Rules and payment network requirements; Conducting fraud prevention, risk monitoring, and security reviews; Verifying merchant identity and business legitimacy; Maintaining insurance and regulatory compliance obligations; Communicating with merchants, customers, carriers, and financial institutions; Improving platform functionality and user experience; Conducting analytics, audits, and internal reporting; Enforcing agreements and protecting legal rights; Complying with applicable laws, regulations, court orders, subpoenas, and governmental requests.",
        "items": [
          "Providing and administering the Services;",
          "Operating shipment protection and related administrative programs;",
          "Processing claims, reimbursements, and customer support requests;",
          "Facilitating merchant onboarding, account verification, and risk assessment;",
          "Managing ACH billing and payment transactions;",
          "Maintaining compliance with NACHA Operating Rules and payment network requirements;",
          "Conducting fraud prevention, risk monitoring, and security reviews;",
          "Verifying merchant identity and business legitimacy;",
          "Maintaining insurance and regulatory compliance obligations;",
          "Communicating with merchants, customers, carriers, and financial institutions;",
          "Improving platform functionality and user experience;",
          "Conducting analytics, audits, and internal reporting;",
          "Enforcing agreements and protecting legal rights;",
          "Complying with applicable laws, regulations, court orders, subpoenas, and governmental requests."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Enshield may use aggregated or de-identified information for lawful business analytics and operational improvement purposes."
      },
      {
        "kind": "heading",
        "text": "5. Legal Basis for Processing"
      },
      {
        "kind": "paragraph",
        "text": "Where applicable under data protection laws, Enshield processes information based on:"
      },
      {
        "kind": "unordered-list",
        "text": "Performance of a contract; Legitimate business interests; Legal compliance obligations; User consent where required.",
        "items": [
          "Performance of a contract;",
          "Legitimate business interests;",
          "Legal compliance obligations;",
          "User consent where required."
        ]
      },
      {
        "kind": "heading",
        "text": "6. Information Sharing and Disclosure"
      },
      {
        "kind": "paragraph",
        "text": "Enshield does not sell personal information."
      },
      {
        "kind": "paragraph",
        "text": "We may disclose information under the following circumstances:"
      },
      {
        "kind": "subheading",
        "text": "A. Service Providers and Processors"
      },
      {
        "kind": "paragraph",
        "text": "We may share information with trusted vendors and service providers assisting with:"
      },
      {
        "kind": "unordered-list",
        "text": "Cloud hosting and infrastructure; Payment processing and ACH transactions; Merchant billing administration; Claims administration; Fraud prevention and identity verification; Customer support operations; Data analytics and monitoring; Security and compliance services; Email, SMS, and communication systems.",
        "items": [
          "Cloud hosting and infrastructure;",
          "Payment processing and ACH transactions;",
          "Merchant billing administration;",
          "Claims administration;",
          "Fraud prevention and identity verification;",
          "Customer support operations;",
          "Data analytics and monitoring;",
          "Security and compliance services;",
          "Email, SMS, and communication systems."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Service providers are contractually required to maintain reasonable confidentiality and security protections."
      },
      {
        "kind": "subheading",
        "text": "B. Financial Institutions and Payment Networks"
      },
      {
        "kind": "paragraph",
        "text": "To facilitate ACH processing and related payment activities, Enshield may share necessary information with:"
      },
      {
        "kind": "unordered-list",
        "text": "Banks and financial institutions; Payment processors; Stripe and affiliated payment processing services; NACHA participants; Risk monitoring providers; Payment verification vendors.",
        "items": [
          "Banks and financial institutions;",
          "Payment processors;",
          "Stripe and affiliated payment processing services;",
          "NACHA participants;",
          "Risk monitoring providers;",
          "Payment verification vendors."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Where possible, sensitive banking and ACH information is collected directly by third-party payment processors through secure hosted interfaces or embedded payment tools rather than directly through Enshield systems."
      },
      {
        "kind": "paragraph",
        "text": "Enshield maintains commercially reasonable procedures intended to support compliance with applicable NACHA Operating Rules, payment security obligations, and commercially reasonable data minimization practices."
      },
      {
        "kind": "subheading",
        "text": "C. Insurance and Regulatory Partners"
      },
      {
        "kind": "paragraph",
        "text": "Where required for regulatory compliance, underwriting, claims administration, program management, or lawful operation of shipment protection programs, Enshield may share relevant information with:"
      },
      {
        "kind": "unordered-list",
        "text": "Licensed insurance carriers; Risk administrators; Claims administrators; Legal counsel; Regulatory authorities.",
        "items": [
          "Licensed insurance carriers;",
          "Risk administrators;",
          "Claims administrators;",
          "Legal counsel;",
          "Regulatory authorities."
        ]
      },
      {
        "kind": "subheading",
        "text": "D. Merchants and Platform Partners"
      },
      {
        "kind": "paragraph",
        "text": "We may share information with the applicable merchant or platform partner necessary to administer shipment protection services, claims handling, reimbursement workflows, customer communications, fraud review, and account management."
      },
      {
        "kind": "subheading",
        "text": "E. Legal Compliance and Protection"
      },
      {
        "kind": "paragraph",
        "text": "We may disclose information when reasonably necessary to:"
      },
      {
        "kind": "unordered-list",
        "text": "Comply with legal obligations or regulatory requirements; Respond to lawful subpoenas, court orders, or governmental requests; Protect the rights, property, safety, and security of Enshield, merchants, customers, or others; Detect, investigate, or prevent fraud, unauthorized activity, money laundering, or unlawful conduct; Enforce agreements and platform policies.",
        "items": [
          "Comply with legal obligations or regulatory requirements;",
          "Respond to lawful subpoenas, court orders, or governmental requests;",
          "Protect the rights, property, safety, and security of Enshield, merchants, customers, or others;",
          "Detect, investigate, or prevent fraud, unauthorized activity, money laundering, or unlawful conduct;",
          "Enforce agreements and platform policies."
        ]
      },
      {
        "kind": "subheading",
        "text": "F. Corporate Transactions"
      },
      {
        "kind": "paragraph",
        "text": "Information may be transferred in connection with a merger, acquisition, financing transaction, restructuring, or sale of assets, subject to applicable confidentiality obligations."
      },
      {
        "kind": "heading",
        "text": "7. Data Retention"
      },
      {
        "kind": "paragraph",
        "text": "Enshield retains information for periods reasonably necessary to:"
      },
      {
        "kind": "unordered-list",
        "text": "Provide and administer the Services; Maintain merchant account records; Process claims and reimbursement requests; Satisfy legal, tax, accounting, insurance, and regulatory obligations; Maintain ACH authorization and transaction records; Resolve disputes and enforce agreements; Detect and investigate fraud or security incidents.",
        "items": [
          "Provide and administer the Services;",
          "Maintain merchant account records;",
          "Process claims and reimbursement requests;",
          "Satisfy legal, tax, accounting, insurance, and regulatory obligations;",
          "Maintain ACH authorization and transaction records;",
          "Resolve disputes and enforce agreements;",
          "Detect and investigate fraud or security incidents."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Certain financial, payment, insurance, and claims-related records may be retained for extended periods where required by law, regulation, contractual obligations, or industry standards."
      },
      {
        "kind": "paragraph",
        "text": "When information is no longer reasonably necessary, Enshield may securely delete, anonymize, or aggregate the information."
      },
      {
        "kind": "heading",
        "text": "8. Cookies and Tracking Technologies"
      },
      {
        "kind": "paragraph",
        "text": "Enshield may use cookies, pixels, and similar technologies to:"
      },
      {
        "kind": "unordered-list",
        "text": "Maintain platform functionality; Authenticate users; Analyze traffic and usage trends; Improve performance and user experience.",
        "items": [
          "Maintain platform functionality;",
          "Authenticate users;",
          "Analyze traffic and usage trends;",
          "Improve performance and user experience."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Users may adjust browser settings to limit cookies; however, certain features of the Services may not function properly if cookies are disabled."
      },
      {
        "kind": "heading",
        "text": "9. Data Security"
      },
      {
        "kind": "paragraph",
        "text": "Enshield implements commercially reasonable administrative, technical, organizational, and physical safeguards designed to protect information against unauthorized access, disclosure, misuse, alteration, or destruction."
      },
      {
        "kind": "paragraph",
        "text": "Security measures may include:"
      },
      {
        "kind": "unordered-list",
        "text": "Access controls and authentication requirements; Encryption technologies where appropriate; Role-based access restrictions; Logging and monitoring systems; Vendor security reviews; Employee confidentiality obligations; Incident response procedures; Fraud monitoring and risk detection tools.",
        "items": [
          "Access controls and authentication requirements;",
          "Encryption technologies where appropriate;",
          "Role-based access restrictions;",
          "Logging and monitoring systems;",
          "Vendor security reviews;",
          "Employee confidentiality obligations;",
          "Incident response procedures;",
          "Fraud monitoring and risk detection tools."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Enshield also maintains policies and procedures intended to support compliance with applicable payment processing obligations, ACH security requirements, insurance-related confidentiality obligations, and commercially reasonable information security practices."
      },
      {
        "kind": "paragraph",
        "text": "Despite these safeguards, no electronic transmission or storage system can be guaranteed completely secure."
      },
      {
        "kind": "heading",
        "text": "10. International Data Transfers"
      },
      {
        "kind": "paragraph",
        "text": "Information may be processed and stored in the United States or other jurisdictions where Enshield or its service providers operate."
      },
      {
        "kind": "paragraph",
        "text": "By using the Services, users acknowledge that information may be transferred to jurisdictions with different data protection laws than their own."
      },
      {
        "kind": "heading",
        "text": "11. User Rights and Choices"
      },
      {
        "kind": "paragraph",
        "text": "Depending on applicable law, users may have rights to:"
      },
      {
        "kind": "unordered-list",
        "text": "Access personal information; Correct inaccurate information; Request deletion of certain information; Object to or restrict certain processing activities; Withdraw consent where processing is based on consent; Request portability of certain information.",
        "items": [
          "Access personal information;",
          "Correct inaccurate information;",
          "Request deletion of certain information;",
          "Object to or restrict certain processing activities;",
          "Withdraw consent where processing is based on consent;",
          "Request portability of certain information."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Enshield may decline requests where permitted or required by law, including where information must be retained for:"
      },
      {
        "kind": "unordered-list",
        "text": "Fraud prevention; Claims administration; Insurance compliance; ACH transaction verification; Financial recordkeeping; Legal and regulatory obligations; Enforcement of agreements.",
        "items": [
          "Fraud prevention;",
          "Claims administration;",
          "Insurance compliance;",
          "ACH transaction verification;",
          "Financial recordkeeping;",
          "Legal and regulatory obligations;",
          "Enforcement of agreements."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Requests may be submitted using the contact information provided below. Enshield may require verification of identity before responding to requests."
      },
      {
        "kind": "heading",
        "text": "12. California Privacy Rights"
      },
      {
        "kind": "paragraph",
        "text": "If applicable, California residents may have additional rights under the California Consumer Privacy Act (“CCPA”) or California Privacy Rights Act (“CPRA”), including the right to:"
      },
      {
        "kind": "unordered-list",
        "text": "Know what personal information is collected; Request deletion of personal information; Correct inaccurate information; Limit certain uses of sensitive personal information; Receive equal service and pricing regardless of exercising privacy rights.",
        "items": [
          "Know what personal information is collected;",
          "Request deletion of personal information;",
          "Correct inaccurate information;",
          "Limit certain uses of sensitive personal information;",
          "Receive equal service and pricing regardless of exercising privacy rights."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Enshield does not sell or share personal information for cross-context behavioral advertising."
      },
      {
        "kind": "heading",
        "text": "13. Children’s Privacy"
      },
      {
        "kind": "paragraph",
        "text": "The Services are not directed to children under 13 years of age, and Enshield does not knowingly collect personal information from children."
      },
      {
        "kind": "paragraph",
        "text": "If Enshield becomes aware that information has been collected from a child in violation of applicable law, we will take reasonable steps to delete such information."
      },
      {
        "kind": "heading",
        "text": "14. Third-Party Services"
      },
      {
        "kind": "paragraph",
        "text": "The Services may contain links or integrations to third-party websites, applications, or services. Enshield is not responsible for the privacy practices or content of third parties."
      },
      {
        "kind": "paragraph",
        "text": "Users should review the privacy policies of those third parties separately."
      },
      {
        "kind": "heading",
        "text": "15. Changes to This Privacy Policy"
      },
      {
        "kind": "paragraph",
        "text": "Enshield may update this Privacy Policy periodically. Updated versions will be posted with a revised “Last Updated” date."
      },
      {
        "kind": "paragraph",
        "text": "Continued use of the Services following publication of changes constitutes acceptance of the updated Privacy Policy."
      },
      {
        "kind": "heading",
        "text": "16. Contact Information"
      },
      {
        "kind": "paragraph",
        "text": "If you have questions regarding this Privacy Policy or Enshield’s privacy practices, please contact:"
      },
      {
        "kind": "paragraph",
        "text": "Enshield Inc. P.O. Box 502"
      },
      {
        "kind": "paragraph",
        "text": "Rexburg, ID 83440 Phone: 208-317-1160 Email: CS@enshield.com"
      }
    ]
  },
  "/terms-of-service": {
    "title": "Enshield Platform Terms of Service",
    "description": "The published Enshield Platform Terms of Service governing access to and use of Enshield services.",
    "sourceUrl": "https://enshield.com/terms-of-service",
    "capturedAt": "2026-08-29",
    "sourceSha256": "6cb88c81b7f753db9f134df90ae58a57401c1993d274072415e69681edd71051",
    "effectiveDate": "April 24, 2026",
    "lastUpdated": "April 24, 2026",
    "blocks": [
      {
        "kind": "paragraph",
        "text": "These Terms of Service (“Terms”) govern access to and use of the Enshield platform, website, APIs, merchant dashboard, integrations, and related services (collectively, the “Platform”) provided by Enshield Inc. (“Enshield,” “we,” “us,” or “our”)."
      },
      {
        "kind": "paragraph",
        "text": "By accessing or using the Platform, you (“Merchant,” “Client,” “you,” or “your”) agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Platform."
      },
      {
        "kind": "heading",
        "text": "1. Eligibility & Authority"
      },
      {
        "kind": "paragraph",
        "text": "You represent and warrant that:"
      },
      {
        "kind": "ordered-list",
        "text": "You are at least eighteen (18) years old; You have the legal authority to enter into these Terms on behalf of yourself or the entity you represent; Your use of the Platform complies with all applicable laws, regulations, and industry requirements; All information provided to Enshield is accurate, complete, and current.",
        "items": [
          "You are at least eighteen (18) years old;",
          "You have the legal authority to enter into these Terms on behalf of yourself or the entity you represent;",
          "Your use of the Platform complies with all applicable laws, regulations, and industry requirements;",
          "All information provided to Enshield is accurate, complete, and current."
        ]
      },
      {
        "kind": "paragraph",
        "text": "If you are entering into these Terms on behalf of a business entity, you represent that you are authorized to bind that entity to these Terms."
      },
      {
        "kind": "heading",
        "text": "2. Description of Services"
      },
      {
        "kind": "paragraph",
        "text": "Enshield provides software, administrative tools, customer communication tools, claims administration workflows, reporting systems, merchant billing functionality, and related services designed to assist merchants in administering package protection and related postdelivery customer service programs."
      },
      {
        "kind": "paragraph",
        "text": "Enshield may provide:"
      },
      {
        "kind": "unordered-list",
        "text": "Checkout integration tools; Merchant dashboards and analytics; Claims intake and review workflows; Customer communication systems; Billing and reconciliation services; API integrations; Reporting and operational support; Administrative support services.",
        "items": [
          "Checkout integration tools;",
          "Merchant dashboards and analytics;",
          "Claims intake and review workflows;",
          "Customer communication systems;",
          "Billing and reconciliation services;",
          "API integrations;",
          "Reporting and operational support;",
          "Administrative support services."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Enshield is a technology and administrative services provider. Certain package protection programs or regulated protection products offered through the Platform may be administered, insured, or supported by Enshield Risk Solutions Inc. (“ERS”) and/or other licensed thirdparty entities where required by applicable law. Enshield may coordinate administrative and customer service functions relating to such programs on behalf of ERS or other authorized providers."
      },
      {
        "kind": "heading",
        "text": "3. Merchant Responsibilities"
      },
      {
        "kind": "paragraph",
        "text": "Merchant agrees to:"
      },
      {
        "kind": "ordered-list",
        "text": "Maintain accurate business and contact information; Promptly cooperate with reasonable operational and compliance requests; Ensure all checkout disclosures and representations to consumers remain accurate; Comply with all applicable consumer protection, privacy, and ecommerce laws; Maintain all required licenses, permits, and authorizations applicable to Merchant’s business; Use the Platform solely for lawful business purposes.",
        "items": [
          "Maintain accurate business and contact information;",
          "Promptly cooperate with reasonable operational and compliance requests;",
          "Ensure all checkout disclosures and representations to consumers remain accurate;",
          "Comply with all applicable consumer protection, privacy, and ecommerce laws;",
          "Maintain all required licenses, permits, and authorizations applicable to Merchant’s business;",
          "Use the Platform solely for lawful business purposes."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Merchant shall not:"
      },
      {
        "kind": "unordered-list",
        "text": "Use the Platform for fraudulent or deceptive activity; Misrepresent coverage, benefits, or protections available to consumers; Interfere with Platform operations or security; Reverse engineer or attempt unauthorized access to the Platform; Use the Platform in violation of applicable law.",
        "items": [
          "Use the Platform for fraudulent or deceptive activity;",
          "Misrepresent coverage, benefits, or protections available to consumers;",
          "Interfere with Platform operations or security;",
          "Reverse engineer or attempt unauthorized access to the Platform;",
          "Use the Platform in violation of applicable law."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Merchant remains solely responsible for:"
      },
      {
        "kind": "unordered-list",
        "text": "Products sold to consumers; Product fulfillment and shipping operations; Customer service unrelated to the package protection program; Tax obligations; Compliance with laws applicable to Merchant’s products and operations.",
        "items": [
          "Products sold to consumers;",
          "Product fulfillment and shipping operations;",
          "Customer service unrelated to the package protection program;",
          "Tax obligations;",
          "Compliance with laws applicable to Merchant’s products and operations."
        ]
      },
      {
        "kind": "heading",
        "text": "4. Consumer Information & Data Processing"
      },
      {
        "kind": "paragraph",
        "text": "In connection with the Platform, Merchant may provide customer information including names, addresses, contact information, order details, shipment details, and transaction information."
      },
      {
        "kind": "paragraph",
        "text": "Merchant represents and warrants that it has all rights and permissions necessary to provide such information to Enshield for purposes of operating the Platform and related services."
      },
      {
        "kind": "paragraph",
        "text": "Enshield may process such information:"
      },
      {
        "kind": "unordered-list",
        "text": "To provide Platform functionality; To administer claims and customer support workflows; To communicate with consumers; To comply with legal and regulatory obligations; To improve Platform functionality and security.",
        "items": [
          "To provide Platform functionality;",
          "To administer claims and customer support workflows;",
          "To communicate with consumers;",
          "To comply with legal and regulatory obligations;",
          "To improve Platform functionality and security."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Enshield does not directly collect, process, or store consumer bank account or ACH payment credentials through the Platform. Where payment information is required, such information may be collected and processed through secure thirdparty payment processors, including Stripe or similar providers, subject to their respective terms and privacy practices."
      },
      {
        "kind": "paragraph",
        "text": "Merchant acknowledges that thirdparty processors may independently process and store payment information pursuant to their own compliance and security standards."
      },
      {
        "kind": "heading",
        "text": "5. Privacy & Communications"
      },
      {
        "kind": "paragraph",
        "text": "Use of the Platform is also governed by the Enshield Privacy Policy, which is incorporated into these Terms by reference."
      },
      {
        "kind": "paragraph",
        "text": "Merchant acknowledges and agrees that:"
      },
      {
        "kind": "unordered-list",
        "text": "Calls with Enshield personnel may be monitored or recorded; Meetings may be transcribed or summarized using AIassisted tools; Customer service interactions may be logged for quality assurance, operational, training, compliance, and dispute resolution purposes.",
        "items": [
          "Calls with Enshield personnel may be monitored or recorded;",
          "Meetings may be transcribed or summarized using AIassisted tools;",
          "Customer service interactions may be logged for quality assurance, operational, training, compliance, and dispute resolution purposes."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Merchant shall ensure that any legally required notices or consents relating to recording or communications are obtained where applicable."
      },
      {
        "kind": "heading",
        "text": "6. Fees & Payment Terms"
      },
      {
        "kind": "paragraph",
        "text": "Merchant agrees to pay all applicable fees associated with use of the Platform and related services."
      },
      {
        "kind": "paragraph",
        "text": "Unless otherwise agreed in writing:"
      },
      {
        "kind": "ordered-list",
        "text": "Fees are due according to the applicable onboarding documents, invoices, merchant agreement, or billing schedules; Enshield may initiate ACH debits or other authorized payment methods for amounts due; Merchant authorizes Enshield and its designated payment processors to process authorized payments; Late payments may accrue interest at the lesser of 1.5% per month or the maximum rate permitted by law; Merchant is responsible for all taxes associated with its use of the Platform, excluding taxes on Enshield’s income.",
        "items": [
          "Fees are due according to the applicable onboarding documents, invoices, merchant agreement, or billing schedules;",
          "Enshield may initiate ACH debits or other authorized payment methods for amounts due;",
          "Merchant authorizes Enshield and its designated payment processors to process authorized payments;",
          "Late payments may accrue interest at the lesser of 1.5% per month or the maximum rate permitted by law;",
          "Merchant is responsible for all taxes associated with its use of the Platform, excluding taxes on Enshield’s income."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Merchant agrees to maintain valid payment information and promptly update any changes."
      },
      {
        "kind": "heading",
        "text": "7. ACH Authorization & NACHA Compliance"
      },
      {
        "kind": "paragraph",
        "text": "By providing bank account information or authorizing electronic payments, Merchant authorizes Enshield, its affiliates, agents, processors, and payment partners, including Stripe and related financial institutions, to initiate electronic ACH debits and credits to Merchant’s designated bank account for:"
      },
      {
        "kind": "unordered-list",
        "text": "Service fees; Billing adjustments; Returned payment corrections; Charge reversals where permitted; Other authorized amounts owed under these Terms or any related merchant agreement.",
        "items": [
          "Service fees;",
          "Billing adjustments;",
          "Returned payment corrections;",
          "Charge reversals where permitted;",
          "Other authorized amounts owed under these Terms or any related merchant agreement."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Merchant authorizes such entries in accordance with applicable NACHA Operating Rules and agrees that this authorization shall remain in full force and effect until revoked in writing and reasonably processed by Enshield."
      },
      {
        "kind": "paragraph",
        "text": "Merchant represents and warrants that:"
      },
      {
        "kind": "ordered-list",
        "text": "It is the authorized owner or signatory of the designated bank account; All payment information provided is accurate and current; It has authority to authorize ACH transactions; It will maintain sufficient funds for authorized transactions.",
        "items": [
          "It is the authorized owner or signatory of the designated bank account;",
          "All payment information provided is accurate and current;",
          "It has authority to authorize ACH transactions;",
          "It will maintain sufficient funds for authorized transactions."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Merchant agrees:"
      },
      {
        "kind": "unordered-list",
        "text": "To comply with applicable NACHA Operating Rules and applicable payment network requirements; Not to improperly dispute or reverse authorized ACH transactions; To promptly notify Enshield of any changes to banking information; That Enshield may retry failed or returned ACH transactions where permitted by law.",
        "items": [
          "To comply with applicable NACHA Operating Rules and applicable payment network requirements;",
          "Not to improperly dispute or reverse authorized ACH transactions;",
          "To promptly notify Enshield of any changes to banking information;",
          "That Enshield may retry failed or returned ACH transactions where permitted by law."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Merchant acknowledges that returned payments, rejected transactions, excessive disputes, or unauthorized return activity may result in:"
      },
      {
        "kind": "unordered-list",
        "text": "Additional processing fees; Suspension of services; Termination of Platform access; Additional risk review or verification requirements.",
        "items": [
          "Additional processing fees;",
          "Suspension of services;",
          "Termination of Platform access;",
          "Additional risk review or verification requirements."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Enshield may maintain records of electronic payment authorizations, payment activity, transaction logs, IP address information, consent records, and related account verification information for compliance, fraud prevention, dispute resolution, audit, operational, and legal purposes."
      },
      {
        "kind": "heading",
        "text": "8. Electronic Communications & ESIGN Consent"
      },
      {
        "kind": "paragraph",
        "text": "Merchant consents to receive communications electronically from Enshield, including:"
      },
      {
        "kind": "unordered-list",
        "text": "Agreements; Invoices; Billing notices; Claims communications; Policy updates; Disclosures; Operational notices; Compliance communications.",
        "items": [
          "Agreements;",
          "Invoices;",
          "Billing notices;",
          "Claims communications;",
          "Policy updates;",
          "Disclosures;",
          "Operational notices;",
          "Compliance communications."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Merchant agrees that electronic signatures, electronic records, and electronic communications satisfy any legal requirement that such communications be in writing."
      },
      {
        "kind": "paragraph",
        "text": "Merchant agrees to maintain accurate contact information and acknowledges responsibility for reviewing electronic communications sent by Enshield."
      },
      {
        "kind": "heading",
        "text": "9. ThirdParty Services & Integrations"
      },
      {
        "kind": "paragraph",
        "text": "The Platform may integrate with thirdparty services including ecommerce platforms, payment processors, shipping providers, CRM systems, analytics tools, or communication providers."
      },
      {
        "kind": "paragraph",
        "text": "Enshield does not control and is not responsible for:"
      },
      {
        "kind": "unordered-list",
        "text": "Thirdparty platform availability; Thirdparty outages or disruptions; Thirdparty security practices; Thirdparty terms or policies.",
        "items": [
          "Thirdparty platform availability;",
          "Thirdparty outages or disruptions;",
          "Thirdparty security practices;",
          "Thirdparty terms or policies."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Use of thirdparty integrations may require Merchant to agree to separate terms with such providers."
      },
      {
        "kind": "heading",
        "text": "8. Intellectual Property"
      },
      {
        "kind": "paragraph",
        "text": "All Platform content, software, workflows, APIs, designs, trademarks, service marks, logos, and related intellectual property are owned by or licensed to Enshield."
      },
      {
        "kind": "paragraph",
        "text": "Subject to these Terms, Enshield grants Merchant a limited, nonexclusive, nontransferable, revocable license to access and use the Platform for Merchant’s internal business purposes."
      },
      {
        "kind": "paragraph",
        "text": "Merchant shall not:"
      },
      {
        "kind": "unordered-list",
        "text": "Copy or reproduce Platform software or materials; Modify or create derivative works; Resell or sublicense access to the Platform; Remove proprietary notices; Use Enshield intellectual property except as expressly permitted.",
        "items": [
          "Copy or reproduce Platform software or materials;",
          "Modify or create derivative works;",
          "Resell or sublicense access to the Platform;",
          "Remove proprietary notices;",
          "Use Enshield intellectual property except as expressly permitted."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Merchant grants Enshield a limited, nonexclusive license to use Merchant’s business name, trademarks, and logos for:"
      },
      {
        "kind": "unordered-list",
        "text": "Identifying Merchant as a customer of Enshield; Marketing materials; Case studies; Website listings; Sales presentations.",
        "items": [
          "Identifying Merchant as a customer of Enshield;",
          "Marketing materials;",
          "Case studies;",
          "Website listings;",
          "Sales presentations."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Merchant may revoke such marketing authorization upon written notice, except for materials already published or distributed."
      },
      {
        "kind": "heading",
        "text": "9. Confidentiality"
      },
      {
        "kind": "paragraph",
        "text": "Each party agrees to protect the other party’s confidential information using reasonable safeguards and not disclose such information except:"
      },
      {
        "kind": "unordered-list",
        "text": "As necessary to perform obligations under these Terms; To professional advisors subject to confidentiality obligations; As required by law, court order, or regulatory authority.",
        "items": [
          "As necessary to perform obligations under these Terms;",
          "To professional advisors subject to confidentiality obligations;",
          "As required by law, court order, or regulatory authority."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Confidential information does not include information that:"
      },
      {
        "kind": "unordered-list",
        "text": "Is publicly available without breach; Was already lawfully known; Is independently developed; Is lawfully obtained from a third party.",
        "items": [
          "Is publicly available without breach;",
          "Was already lawfully known;",
          "Is independently developed;",
          "Is lawfully obtained from a third party."
        ]
      },
      {
        "kind": "heading",
        "text": "10. Compliance & Prohibited Activities"
      },
      {
        "kind": "paragraph",
        "text": "Merchant represents and warrants that neither Merchant nor its principals:"
      },
      {
        "kind": "unordered-list",
        "text": "Are subject to sanctions administered by OFAC or similar governmental authorities; Are listed on applicable denied or restricted party lists; Will use the Platform for unlawful transactions or prohibited businesses.",
        "items": [
          "Are subject to sanctions administered by OFAC or similar governmental authorities;",
          "Are listed on applicable denied or restricted party lists;",
          "Will use the Platform for unlawful transactions or prohibited businesses."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Merchant agrees to comply with all applicable:"
      },
      {
        "kind": "unordered-list",
        "text": "Antimoney laundering laws; Sanctions regulations; Consumer protection laws; Data privacy laws; Ecommerce and financial regulations.",
        "items": [
          "Antimoney laundering laws;",
          "Sanctions regulations;",
          "Consumer protection laws;",
          "Data privacy laws;",
          "Ecommerce and financial regulations."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Enshield reserves the right to suspend or terminate access where it reasonably believes Merchant may be engaged in unlawful, fraudulent, deceptive, or prohibited conduct."
      },
      {
        "kind": "heading",
        "text": "11. Availability & Service Modifications"
      },
      {
        "kind": "paragraph",
        "text": "Enshield may modify, suspend, or discontinue portions of the Platform at any time."
      },
      {
        "kind": "paragraph",
        "text": "While Enshield seeks to maintain reliable service availability, the Platform is provided on an “as available” basis and uninterrupted access is not guaranteed."
      },
      {
        "kind": "paragraph",
        "text": "Scheduled maintenance, emergency maintenance, thirdparty outages, and circumstances beyond Enshield’s control may affect availability."
      },
      {
        "kind": "heading",
        "text": "12. Disclaimers"
      },
      {
        "kind": "paragraph",
        "text": "THE PLATFORM IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY LAW, ENSHIELD DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING:"
      },
      {
        "kind": "unordered-list",
        "text": "MERCHANTABILITY; FITNESS FOR A PARTICULAR PURPOSE; NONINFRINGEMENT; ACCURACY OR RELIABILITY OF INFORMATION; UNINTERRUPTED OR ERRORFREE OPERATION.",
        "items": [
          "MERCHANTABILITY;",
          "FITNESS FOR A PARTICULAR PURPOSE;",
          "NONINFRINGEMENT;",
          "ACCURACY OR RELIABILITY OF INFORMATION;",
          "UNINTERRUPTED OR ERRORFREE OPERATION."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Enshield does not guarantee:"
      },
      {
        "kind": "unordered-list",
        "text": "Specific financial results; Consumer participation rates; Revenue generation; Loss reduction; Approval by any thirdparty processor or regulator.",
        "items": [
          "Specific financial results;",
          "Consumer participation rates;",
          "Revenue generation;",
          "Loss reduction;",
          "Approval by any thirdparty processor or regulator."
        ]
      },
      {
        "kind": "heading",
        "text": "13. Limitation of Liability"
      },
      {
        "kind": "paragraph",
        "text": "TO THE MAXIMUM EXTENT PERMITTED BY LAW, ENSHIELD SHALL NOT BE LIABLE FOR:"
      },
      {
        "kind": "unordered-list",
        "text": "INDIRECT DAMAGES; INCIDENTAL DAMAGES; CONSEQUENTIAL DAMAGES; SPECIAL DAMAGES; PUNITIVE DAMAGES; LOST PROFITS; LOST REVENUE; LOSS OF DATA; BUSINESS INTERRUPTION.",
        "items": [
          "INDIRECT DAMAGES;",
          "INCIDENTAL DAMAGES;",
          "CONSEQUENTIAL DAMAGES;",
          "SPECIAL DAMAGES;",
          "PUNITIVE DAMAGES;",
          "LOST PROFITS;",
          "LOST REVENUE;",
          "LOSS OF DATA;",
          "BUSINESS INTERRUPTION."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Enshield’s total aggregate liability arising out of or relating to the Platform or these Terms shall not exceed the total fees paid by Merchant to Enshield during the six (6) months preceding the event giving rise to the claim."
      },
      {
        "kind": "paragraph",
        "text": "Some jurisdictions do not allow certain liability limitations, so portions of this section may not apply."
      },
      {
        "kind": "heading",
        "text": "14. Indemnification"
      },
      {
        "kind": "paragraph",
        "text": "Merchant agrees to defend, indemnify, and hold harmless Enshield and its officers, directors, employees, contractors, affiliates, insurers, and partners from any claims, liabilities, damages, losses, costs, or expenses arising from:"
      },
      {
        "kind": "unordered-list",
        "text": "Merchant’s breach of these Terms; Merchant’s products or services; Merchant’s violation of law; Fraudulent or deceptive conduct; Consumer disputes unrelated to Enshield’s direct actions; Merchant content or data.",
        "items": [
          "Merchant’s breach of these Terms;",
          "Merchant’s products or services;",
          "Merchant’s violation of law;",
          "Fraudulent or deceptive conduct;",
          "Consumer disputes unrelated to Enshield’s direct actions;",
          "Merchant content or data."
        ]
      },
      {
        "kind": "heading",
        "text": "15. Term & Termination"
      },
      {
        "kind": "paragraph",
        "text": "These Terms remain effective until terminated."
      },
      {
        "kind": "paragraph",
        "text": "Either party may terminate access to the Platform:"
      },
      {
        "kind": "unordered-list",
        "text": "Upon written notice if the other party materially breaches these Terms and fails to cure within thirty (30) days; Immediately for fraud, unlawful conduct, sanctions concerns, or misuse of the Platform; As otherwise permitted in a separate merchant agreement.",
        "items": [
          "Upon written notice if the other party materially breaches these Terms and fails to cure within thirty (30) days;",
          "Immediately for fraud, unlawful conduct, sanctions concerns, or misuse of the Platform;",
          "As otherwise permitted in a separate merchant agreement."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Upon termination:"
      },
      {
        "kind": "unordered-list",
        "text": "Merchant access may be disabled; Outstanding payment obligations survive; Certain operational data may be retained as required by law or legitimate business purposes.",
        "items": [
          "Merchant access may be disabled;",
          "Outstanding payment obligations survive;",
          "Certain operational data may be retained as required by law or legitimate business purposes."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Sections intended to survive termination shall survive, including confidentiality, payment obligations, intellectual property protections, disclaimers, limitation of liability, and indemnification obligations."
      },
      {
        "kind": "heading",
        "text": "16. Governing Law & Dispute Resolution"
      },
      {
        "kind": "paragraph",
        "text": "These Terms shall be governed by and construed in accordance with the laws of the State of Idaho, without regard to conflict of law principles."
      },
      {
        "kind": "paragraph",
        "text": "Any dispute arising from or relating to these Terms shall be resolved exclusively in the state or federal courts located in Ada County, Idaho, and each party consents to such jurisdiction and venue."
      },
      {
        "kind": "heading",
        "text": "17. Force Majeure"
      },
      {
        "kind": "paragraph",
        "text": "Enshield shall not be liable for delays or failures resulting from causes beyond its reasonable control, including:"
      },
      {
        "kind": "unordered-list",
        "text": "Natural disasters; Internet outages; Cyberattacks; Labor disputes; Governmental actions; Utility failures; Acts of war or terrorism; Thirdparty service interruptions.",
        "items": [
          "Natural disasters;",
          "Internet outages;",
          "Cyberattacks;",
          "Labor disputes;",
          "Governmental actions;",
          "Utility failures;",
          "Acts of war or terrorism;",
          "Thirdparty service interruptions."
        ]
      },
      {
        "kind": "heading",
        "text": "18. Changes to Terms"
      },
      {
        "kind": "paragraph",
        "text": "Enshield may update these Terms from time to time."
      },
      {
        "kind": "paragraph",
        "text": "Updated Terms may be posted through the Platform or otherwise communicated to Merchant. Continued use of the Platform after updated Terms become effective constitutes acceptance of the revised Terms."
      },
      {
        "kind": "heading",
        "text": "19. Miscellaneous"
      },
      {
        "kind": "paragraph",
        "text": "These Terms constitute the entire agreement between the parties regarding the Platform unless supplemented by a separate written merchant agreement."
      },
      {
        "kind": "paragraph",
        "text": "If any provision is held unenforceable, the remaining provisions shall remain in full force and effect."
      },
      {
        "kind": "paragraph",
        "text": "Merchant may not assign these Terms without Enshield’s prior written consent. Enshield may assign these Terms in connection with a merger, acquisition, restructuring, or sale of assets."
      },
      {
        "kind": "paragraph",
        "text": "Failure to enforce any provision shall not constitute a waiver."
      },
      {
        "kind": "heading",
        "text": "20. Contact Information"
      },
      {
        "kind": "paragraph",
        "text": "Enshield Inc."
      },
      {
        "kind": "paragraph",
        "text": "P.O. Box 502 Rexburg, ID 83440"
      },
      {
        "kind": "paragraph",
        "text": "Phone: (208) 3171160"
      },
      {
        "kind": "paragraph",
        "text": "Email: cs@enshield.com"
      },
      {
        "kind": "paragraph",
        "text": "Website: https://www.enshield.com"
      }
    ]
  },
  "/learn-more": {
    "title": "Learn More",
    "description": "Learn more about Enshield Package Protection, eligible shipping issues, claims, limitations, and availability.",
    "sourceUrl": "https://enshield.com/learn-more",
    "capturedAt": "2026-08-29",
    "sourceSha256": "12c644a0279b8378a977a65f70457182609415442998904757046c48ccadd097",
    "blocks": [
      {
        "kind": "paragraph",
        "text": "Learn more about Enshield Package Protection, including eligible shipping issues, claims, limitations, and availability."
      }
    ],
    "download": {
      "href": "/legal/learn-more-2026-08-06.pdf",
      "label": "Download Enshield Learn More v.08.06.26 PDF",
      "sha256": "12c644a0279b8378a977a65f70457182609415442998904757046c48ccadd097"
    }
  }
};
